import React, { useCallback, useEffect, useRef, useState } from 'react';
import './styles.css';
import { useT } from '../../i18n/LanguageProvider';

interface Props {
	/** Called once the open animation finishes and the card should be revealed. */
	onOpened: () => void;
	recipientName?: string;
	senderName?: string;
	/** Optional cover image shown as the card rising out of the envelope. */
	coverImageUrl?: string;
}

// Total duration of the open sequence (flap + letter rise + overlay fade).
const OPEN_DURATION = 1700;

/**
 * "Coming out of the envelope" intro shown to recipients before the card.
 * Tap the envelope to play the open animation, then hand off to the card viewer.
 */
const EnvelopeIntro: React.FC<Props> = ({ onOpened, recipientName, senderName, coverImageUrl }) => {
	const { t } = useT();
	const [opening, setOpening] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout>>();

	const open = useCallback(() => {
		if (opening) return;
		setOpening(true);
		timer.current = setTimeout(onOpened, OPEN_DURATION);
	}, [opening, onOpened]);

	useEffect(() => () => clearTimeout(timer.current), []);

	const greeting = recipientName ? `${t('viewcard.hi', { defaultValue: 'Hi' })} ${recipientName},` : t('viewcard.youHaveACard');
	const sub = senderName ? t('viewcard.sentYouACard', { sender: senderName }) : t('viewcard.tapEnvelopeToOpen');

	return (
		<div className={`env-overlay${opening ? ' opening' : ''}`}>
			<div className="env-greeting">
				<div className="env-name">{greeting}</div>
				<div className="env-sub">{sub}</div>
			</div>

			<div
				className={`env-scene${opening ? ' opening' : ''}`}
				onClick={open}
				role="button"
				tabIndex={0}
				aria-label="Open your card"
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') open();
				}}
			>
				<div className="envelope">
					<div className="env-back" />
					<div
						className="env-letter"
						style={coverImageUrl ? { backgroundImage: `url(${coverImageUrl})` } : undefined}
					/>
					<div className="env-front" />
					<div className="env-flap" />
				</div>
			</div>

			{!opening && <div className="env-hint">{t('viewcard.tapToOpen')}</div>}
		</div>
	);
};

export default EnvelopeIntro;
